/**
 * Thrive & HubSpot: what nu-sync mirrors about the person — the Thrive
 * contact(s), and one timeline of notes and outreaches.
 *
 * Three answers that are not a timeline, each with its own words:
 *   - 503 (`nu_sync_unavailable`): nu-sync did not answer. Says so, offers a
 *     retry, and leaves the rest of the panel alone;
 *   - 200 with nothing: "never contacted" is information (04), so it is a
 *     sentence, not a blank;
 *   - `has_more`: the timeline is capped per kind; the counts are not.
 */
import { Calendar, Mail, StickyNote } from "lucide-react";
import { useState } from "react";

import type {
  PersonDetail,
  PersonRelationshipResponse,
  RelationshipItemOut,
} from "@nu-assist/contracts";
import { Spinner } from "@nu-assist/ui";

import { PeopleApiError } from "../../clients/people.client";
import { EmptyLine, PanelSection, TimelineLine } from "./PanelSection";
import { fmtDate, fmtDateTime, personFirstName } from "./format";
import { PANEL_PAGE_SIZE, PanelPager, usePagedList } from "./paging";

export const RELATIONSHIP_UNAVAILABLE = "Thrive & HubSpot history is unavailable right now.";
export const RELATIONSHIP_EMPTY =
  "No emails, calls or notes with this person in Thrive or HubSpot.";

export type RelationshipFilter = "all" | "email" | "meeting" | "note";

/** Which filter an item answers to. Calls and every non-email outreach sit
 *  under Meetings: nu-sync's outreach `type` is free text (`F2F Meeting`,
 *  `Phone Call`, `Meeting`, …), and "Emails" must mean emails only. */
export function itemKind(item: RelationshipItemOut): Exclude<RelationshipFilter, "all"> {
  if (item.kind === "note") return "note";
  return /email/i.test(item.type ?? "") ? "email" : "meeting";
}

export function isNuSyncUnavailable(error: unknown): boolean {
  return error instanceof PeopleApiError && error.status === 503;
}

export function isRelationshipEmpty(data: PersonRelationshipResponse): boolean {
  return data.contacts.length === 0 && data.items.length === 0;
}

const FILTERS: Array<{ value: RelationshipFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "email", label: "Emails" },
  { value: "meeting", label: "Meetings" },
  { value: "note", label: "Notes" },
];

function ItemIcon({ kind }: { kind: Exclude<RelationshipFilter, "all"> }) {
  const cls = "h-4 w-4";
  if (kind === "email") return <Mail className={cls} aria-hidden="true" />;
  if (kind === "meeting") return <Calendar className={cls} aria-hidden="true" />;
  return <StickyNote className={cls} aria-hidden="true" />;
}

function sourceName(source: string | null | undefined): string {
  if (source === "hubspot") return "HubSpot";
  if (source === "thrive") return "Thrive";
  return source ?? "";
}

function ItemRow({
  item,
  first,
  last,
}: {
  item: RelationshipItemOut;
  first: string;
  /** The last item on the page draws no line below it. */
  last: boolean;
}) {
  const kind = itemKind(item);
  const label =
    kind === "note"
      ? item.subject || "Note"
      : item.type || (kind === "email" ? "Email" : "Meeting");
  return (
    <li className="relative flex gap-3" data-testid="person-sync-item" data-kind={kind}>
      {!last && <TimelineLine />}
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-border bg-surface text-text-secondary">
        <ItemIcon kind={kind} />
      </span>
      <div className="min-w-0 pt-1">
        <div className="text-caption tabular-nums text-text-muted">
          {[item.at ? fmtDateTime(item.at) : "Undated", sourceName(item.source)]
            .filter(Boolean)
            .join(" · ")}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-2 text-body text-text">
          <span className="font-semibold">{label}</span>
          <span aria-hidden="true">·</span>
          {item.author?.name ? (
            <span className="font-medium">{item.author.name}</span>
          ) : (
            <span className="text-text-muted" data-testid="person-sync-no-author">
              No author recorded
            </span>
          )}
          {item.direction === "outbound" && (
            <span className="text-caption text-text-secondary">↗ to {first}</span>
          )}
          {item.direction === "inbound" && (
            <span className="text-caption text-success-ink">↙ reply from {first}</span>
          )}
          {item.duration_minutes != null && (
            <span className="text-caption text-text-muted">{item.duration_minutes} min</span>
          )}
        </div>
        {(item.body || (item.subject && kind !== "note")) && (
          <div className="mt-1 rounded-md border border-border bg-bg p-2">
            {item.subject && kind !== "note" && (
              <div className="text-label text-text">{item.subject}</div>
            )}
            {item.body && (
              <p className="line-clamp-6 whitespace-pre-line text-caption text-text-secondary">
                {item.body}
                {item.truncated && " …"}
              </p>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export function RelationshipTab({
  person,
  data,
  error,
  isLoading,
  onRetry,
}: {
  person: PersonDetail;
  data: PersonRelationshipResponse | undefined;
  error: unknown;
  isLoading: boolean;
  onRetry: () => void;
}) {
  const [filter, setFilter] = useState<RelationshipFilter>("all");
  const first = personFirstName(person);
  const shown = (data?.items ?? []).filter((it) => filter === "all" || itemKind(it) === filter);
  // A filter chip is a different list, so it starts on page 1.
  const paged = usePagedList(shown, `${person.handle}|${filter}`);

  if (isLoading) {
    return (
      <PanelSection title="Thrive & HubSpot" testId="person-sync">
        <div className="flex items-center gap-2 text-body text-text-secondary">
          <Spinner /> Loading Thrive &amp; HubSpot history…
        </div>
      </PanelSection>
    );
  }
  if (error || !data) {
    return (
      <PanelSection title="Thrive & HubSpot" testId="person-sync">
        <EmptyLine testId="person-sync-unavailable">
          {isNuSyncUnavailable(error)
            ? RELATIONSHIP_UNAVAILABLE
            : "Could not load Thrive & HubSpot history."}
        </EmptyLine>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Try again
        </button>
      </PanelSection>
    );
  }
  if (isRelationshipEmpty(data)) {
    return (
      <PanelSection title="Thrive & HubSpot" testId="person-sync">
        <EmptyLine testId="person-sync-empty">{RELATIONSHIP_EMPTY}</EmptyLine>
      </PanelSection>
    );
  }

  const counts: Record<RelationshipFilter, number> = {
    all: data.items.length,
    email: 0,
    meeting: 0,
    note: 0,
  };
  for (const it of data.items) counts[itemKind(it)] += 1;
  const totalOnRecord = data.summary.outreaches + data.summary.notes;

  return (
    <PanelSection title="Thrive & HubSpot" testId="person-sync">
      {data.contacts.map((c, i) => (
        <dl
          key={c.thrive_id ?? c.hubspot_id ?? i}
          className="mb-4 grid grid-cols-2 gap-3 text-body sm:grid-cols-4"
          data-testid="person-sync-contact"
        >
          <div>
            <dt className="text-caption text-text-muted">Thrive contact</dt>
            <dd className="text-text">
              {c.thrive_id ? `#${c.thrive_id}` : "Not in Thrive"}
              {c.lifecycle && ` · ${c.lifecycle}`}
            </dd>
          </div>
          <div>
            <dt className="text-caption text-text-muted">Owner</dt>
            <dd className="text-text">{c.owner?.name ?? "Unassigned"}</dd>
          </div>
          <div>
            <dt className="text-caption text-text-muted">In Thrive as</dt>
            <dd className="text-text">{[c.title, c.company].filter(Boolean).join(", ") || "—"}</dd>
          </div>
          <div>
            <dt className="text-caption text-text-muted">Created · last sync</dt>
            <dd className="tabular-nums text-text">
              {fmtDate(c.created_at) || "—"} · {fmtDate(c.last_sync_at) || "—"}
            </dd>
          </div>
        </dl>
      ))}

      <fieldset className="mb-3 flex flex-wrap gap-2">
        <legend className="sr-only">Filter</legend>
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            aria-pressed={filter === f.value}
            data-testid={`person-sync-filter-${f.value}`}
            onClick={() => setFilter(f.value)}
            className={`rounded-pill border px-3 py-1 text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
              filter === f.value
                ? "border-accent bg-accent-muted text-accent-ink"
                : "border-border text-text-secondary hover:border-border-hover"
            }`}
          >
            {f.label} <span className="tabular-nums">{counts[f.value]}</span>
          </button>
        ))}
      </fieldset>

      {shown.length === 0 ? (
        <EmptyLine>Nothing of this kind on record.</EmptyLine>
      ) : (
        <ol className="flex flex-col gap-4" data-testid="person-sync-timeline">
          {paged.items.map((it, i) => (
            <ItemRow
              key={`${it.kind}-${it.at ?? "undated"}-${(paged.page - 1) * PANEL_PAGE_SIZE + i}`}
              item={it}
              first={first}
              last={i === paged.items.length - 1}
            />
          ))}
        </ol>
      )}
      <PanelPager paged={paged} noun="item" testId="person-sync-pager" />
      {data.has_more && (
        <p className="mt-3 text-caption text-text-muted" data-testid="person-sync-has-more">
          Showing the newest {data.items.length} of {totalOnRecord} on record. The rest are in
          Thrive and HubSpot.
        </p>
      )}
    </PanelSection>
  );
}
